import { CommonModule } from '@angular/common';
import { Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { getApp } from 'firebase/app';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { doc, getFirestore, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import type { Session } from '@supabase/supabase-js';
import { AuthService } from '../services/auth.service';
import { LoginService } from '../services/login.service';
import { MaccabiPatientUpdate, MaccabiPendingInvite, MaccabiService } from '../services/maccabi.service';
import { SessionService } from '../services/session.service';
import { TranslationService } from '../services/translation.service';
import { BrandService } from '../../services/brand-config.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { environment } from '../../environments/environment';
import { ClinicMembership, ClinicPublicConfig, ClinicService } from '../services/clinic.service';

interface MaccabiPatient {
  guid: string | null;
  name: string;
  idLast4: string;
  idNumber?: string;
  email?: string;
  status: 'updated' | 'stale' | 'pending';
  meta: string;
  badge: string;
  badgeBg: string;
  badgeColor: string;
  dot: string;
  updated?: string;
  reminder?: boolean;
  group: 'today' | 'more';
  inviteId?: number;
  inviteUrl?: string;
  // Live doctor<->patient link id (for the doctor-side detach action).
  linkId?: number;
  // The invite was sent (currently via Supabase email; SMS not wired yet). Named
  // channel-neutrally — the old inviteSmsSent read as "sent by SMS" for an EMAIL
  // invite and leaked that into the doctor-facing copy (danna-595).
  inviteSent?: boolean;
  inviteExpired?: boolean;
}

@Component({
  selector: 'app-maccabi-portal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './maccabi-portal.component.html',
  styleUrl: './maccabi-portal.component.scss',
  // The portal is Hebrew-first (rtl) but also served at /en (and /en-GB, /en-IN).
  // Drive the whole component's text direction from the current language so English
  // copy + punctuation align left instead of inheriting the hardcoded rtl.
  host: { '[attr.dir]': 'dir' },
})
export class MaccabiPortalComponent implements OnInit, OnDestroy {
  view: 'landing' | 'patients' | 'approve' | 'setup' = 'landing';
  overlay: 'login' | 'register' | 'twofa' | 'terms' | null = null;

  // First-time setup wizard (runs ONCE, right after registration + email verify,
  // never on a plain login). Two steps, "skip for now" at any step lands on a
  // functional empty patient list. See enterSetup()/finishSetup()/skipSetup().
  setupStep: 1 | 2 = 1;
  setupClinic = '';
  setupCredential = '';
  setupFirstPatientEmail = '';
  setupTermsAccepted = false;
  // Step 2 delivery method (Danna layout): a single CHOICE between an email invite
  // and the reusable personal link (not two independent checkboxes). The legacy
  // setupChannelEmail/Link derive from it, so finishSetup still sends the email
  // invite only when the email method is chosen AND an email is filled.
  setupMethod: 'email' | 'link' = 'email';
  get setupChannelEmail(): boolean { return this.setupMethod === 'email'; }
  get setupChannelLink(): boolean { return this.setupMethod === 'link'; }
  setupBusy = false;
  // Whether the registration flow that is running should open the wizard after
  // the email-code step (true for register, false for a normal login).
  private pendingSetupWizard = false;

  // The doctor's reusable personal invite link (markable.life/i/<token>), shown
  // on the wizard + first-day screen with copy-on-click and a copied confirmation.
  personalLink = '';
  personalLinkCopied = false;
  private personalLinkLoading = false;
  registrationDone = false;
  query = '';
  statusFilter: 'all' | MaccabiPatient['status'] = 'all';
  showAddPatient = false;
  smsSent = false;
  inviteSmsSkipped = false;
  inviteError = '';
  loadingPatients = false;
  // Professional-verification gate: true while the account is held for a manual
  // registry check (server reports verified:false). Shows the pending screen
  // instead of the patient list; blocks patient actions until a human clears it.
  pendingVerification = false;

  loginEmail = '';
  loginPassword = '';
  doctorPhone = '';
  loginError = '';
  loginInfo = '';
  loginBusy = false;
  // Guard so an incoming magic-link / OAuth Supabase session is bridged into a
  // server session exactly once (see bridgePortalSession).
  private portalBridgeBusy = false;
  showLoginPassword = false;
  loginCapsOn = false;
  verifyBusy = false;
  inviteBusy = false;
  approveBusy = false;
  reminderBusy = false;
  twoFaCode = '';
  twoFaChallengeId: number | null = null;
  twoFaDebugCode = '';
  twoFaSmsSkipped = false;
  twoFaSmsBusy = false;
  registerFullName = '';
  registerLicense = '';
  registerEmail = '';
  registerPassword = '';
  registerPasswordConfirm = '';
  registerPhone = '';
  registerCode = '';
  registerChallengeId: number | null = null;
  registerDebugCode = '';
  registerSmsSkipped = false;
  registerSmsBusy = false;
  registerPhoneMasked = '';
  registerStep: 'details' | 'sms' = 'details';
  registerError = '';
  registerBusy = false;
  registerTermsAccepted = false;

  // Terms-of-use viewer (opened from the "accept terms" link on registration).
  // The text is loaded per-language from a static asset and cached.
  termsHtml = '';
  termsLoading = false;
  termsError = '';
  private termsCache: { [lang: string]: string } = {};
  private termsReturnOverlay: 'login' | 'register' | 'setup' = 'register';

  patients: MaccabiPatient[] = [];
  pendingInvites: MaccabiPendingInvite[] = [];
  // Stage 5: "new reports" band + review panel (transcription only, arrival order).
  updates: MaccabiPatientUpdate[] = [];
  reviewUpdate: MaccabiPatientUpdate | null = null;
  reviewAgenda = false;
  reviewRecheck = false;
  reviewCopied = false;
  addPhone = '';
  addDob = '';
  addEmail = '';
  lastInviteUrl = '';
  copiedInviteUrl = '';

  // ─── clinic (multi-tenant) ───────────────────────────────────────────────
  // The clinic of the landing URL (/app/doctor/<slug>): drives the co-brand,
  // the signup policy and which clinic a new doctor joins.
  clinicCfg: ClinicPublicConfig | null = null;
  clinicSlug = '';
  memberInviteToken = '';
  memberInvitePreview: any = null;
  // The signed-in doctor's memberships (from the patients payload).
  memberships: ClinicMembership[] = [];
  selectedClinic: string | null = null;
  detachBusyLinkId: number | null = null;

  approveToken = '';
  approveInfo: any = null;
  approveStatus = '';
  // True once the share was approved, so the screen can offer a "start your
  // test" CTA instead of dead-ending on the confirmation text.
  approveSucceeded = false;
  private readonly backgroundRefreshMs = 30000;
  // Sticky "this tab is in the Maccabi doctor portal" marker. The auth-error
  // handler / rest error path only spare the app session when the CURRENT path
  // looks like a doctor route; but a background auth refresh can transiently
  // sit on /app/loading (a patient route), so a stray patient-endpoint 401 there
  // would clear the doctor's session and bounce her to ?login=1. This flag keeps
  // the doctor session protected across those transient non-doctor moments.
  private readonly maccabiDoctorSessionKey = 'markable:maccabiDoctorSession';
  private patientRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private firebaseUnsubscribe: Unsubscribe | null = null;
  private firebaseConnectPromise: Promise<void> | null = null;
  private readonly subscriptions = new Subscription();
  private readonly focusHandler = () => {
    if (this.view === 'patients' && this.sessionService.session) {
      this.loadPatients(false);
    }
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private zone: NgZone,
    private authService: AuthService,
    private loginService: LoginService,
    public sessionService: SessionService,
    private maccabiService: MaccabiService,
    private translationService: TranslationService,
    private brandService: BrandService,
    private clinics: ClinicService,
  ) {}

  /** Our own brand logo (Markable/Peritale), used across every portal screen. */
  get logoSrc(): string {
    return this.brandService.logoSrc;
  }

  ngOnInit(): void {
    // This component only mounts on the doctor portal routes, so mark the tab
    // as a doctor session for the whole visit (see maccabiDoctorSessionKey).
    this.rememberDoctorPortalSession();
    this.syncViewFromUrl();
    // An invited doctor arrives via an emailed magic link that lands on
    // /app/doctor/patients#access_token=... . supabase-js is configured
    // flowType:'pkce', which REJECTS an implicit-fragment magic link inside
    // detectSessionInUrl ("Not a valid PKCE flow url."), so nothing signs her in
    // and she would bounce to a login card she has no password for. Consume the
    // fragment ourselves (BEFORE any bounce below) and exchange it for a server
    // session. Its guard (portalBridgeBusy) is set synchronously so the login
    // bounces further down skip while the sign-in completes.
    void this.consumeMagicLinkFragment();
    window.addEventListener('focus', this.focusHandler);
    this.subscriptions.add(
      this.sessionService.session$.subscribe((session) => {
        if (this.view !== 'patients') return;
        if (session) {
          this.loadPatients();
          this.syncPatientListLiveUpdates();
        } else if (!this.authBridgePending()) {
          this.openLogin();
        }
      }),
    );
    // Bridge a magic-link / OAuth Supabase session into an app server session on
    // the doctor portal. AppComponent's global auth handler deliberately skips
    // doctor routes, so an invited doctor who clicks the emailed magic link
    // (landing on /app/doctor/patients#access_token=...) would otherwise never get
    // a server session and would bounce to a login card she has no password for.
    // Consuming the Supabase session here signs her in and lands her on her list.
    this.subscriptions.add(
      this.authService.session$.subscribe((session) => {
        void this.bridgePortalSession(session);
      }),
    );
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => {
          this.syncViewFromUrl();
          if (this.view === 'patients' && this.sessionService.session) {
            this.loadPatients();
          }
          this.syncPatientListLiveUpdates();
          if (this.view === 'approve') {
            this.loadInviteForApproval();
          }
        }),
    );

    if (this.view === 'patients' && !this.sessionService.session && !this.authBridgePending()) {
      this.openLogin();
    }
    if (this.view === 'patients' && this.sessionService.session) {
      const isSessionBroadcast = this.sessionService.session$.value === this.sessionService.session;
      if (!isSessionBroadcast) {
        this.loadPatients();
      }
    }
    if (this.view === 'approve') {
      this.loadInviteForApproval();
    }
    this.syncPatientListLiveUpdates();
  }

  ngOnDestroy(): void {
    window.removeEventListener('focus', this.focusHandler);
    this.subscriptions.unsubscribe();
    this.stopPatientListLiveUpdates();
  }

  get groupedPatients(): { title: string; extraTop?: boolean; items: MaccabiPatient[] }[] {
    const filtered = this.filteredPatients;
    return [
      { title: this.translationService.translate('maccabi.patients.groupToday'), items: filtered.filter((patient) => patient.group === 'today') },
      { title: this.translationService.translate('maccabi.patients.groupMore'), extraTop: true, items: filtered.filter((patient) => patient.group === 'more') },
    ].filter((group) => group.items.length);
  }

  get updatedCount(): number {
    return this.patients.filter((patient) => patient.status === 'updated').length;
  }

  get doctorDisplayName(): string {
    const user = this.sessionService.session?.user;
    const fullName = `${user?.fname || ''} ${user?.lname || ''}`.trim();
    return fullName || user?.email || '';
  }

  openLogin(): void {
    this.syncRegisterFieldsToLogin();
    this.overlay = 'login';
  }

  // Front-door landing (clinician marketing page): the sample-report lightbox and
  // the sticky mobile "get my link" CTA that appears once the hero CTA scrolls away.
  reportLightbox = false;
  showStickyCta = false;

  /** Primary landing CTA. A solo clinician gets their own link (self-registration);
   *  an invite-only clinic has no self-signup, so it lands on sign-in instead. */
  getMyLink(): void {
    if (this.inviteOnly) {
      this.openLogin();
      return;
    }
    this.openRegister();
  }

  openReportLightbox(): void {
    this.reportLightbox = true;
  }

  closeReportLightbox(): void {
    this.reportLightbox = false;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.reportLightbox) this.reportLightbox = false;
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    // Sticky mobile CTA shows once the reader has scrolled past the hero CTA.
    const show = this.view === 'landing' && window.scrollY > 420;
    if (show !== this.showStickyCta) this.showStickyCta = show;
  }

  // Landing scroll cue: the full-height split hero hid the front-door sections
  // below it (a big image, no hint you can scroll). This jumps to them.
  scrollToFrontDoor(): void {
    try {
      document
        .querySelector('app-maccabi-portal .fd-sections')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch {
      /* noop */
    }
  }

  openRegister(): void {
    this.registrationDone = false;
    this.registerError = '';
    this.registerStep = 'details';
    this.registerCode = '';
    this.registerChallengeId = null;
    this.registerDebugCode = '';
    this.registerSmsSkipped = false;
    this.registerSmsBusy = false;
    this.registerPhoneMasked = '';
    this.syncLoginFieldsToRegister();
    this.overlay = 'register';
  }

  /** Text direction for the whole portal, driven by the active language (rtl for
   *  Hebrew, ltr for English variants). Bound to the host `dir` attribute. */
  get dir(): 'rtl' | 'ltr' {
    return this.translationService.getCurrentLanguage() === 'he' ? 'rtl' : 'ltr';
  }

  get termsDir(): 'rtl' | 'ltr' {
    return this.dir;
  }

  /** Open the terms-of-use viewer over the current overlay, loading the text for
   *  the active language. Returning closes back to where it was opened from so
   *  the registration form keeps its state. */
  async openTerms(from: 'login' | 'register' | 'setup' = 'register'): Promise<void> {
    this.termsReturnOverlay = from;
    this.overlay = 'terms';
    const lang = this.translationService.getCurrentLanguage() === 'he' ? 'he' : 'en';
    if (this.termsCache[lang]) {
      this.termsHtml = this.termsCache[lang];
      return;
    }
    this.termsLoading = true;
    this.termsError = '';
    this.termsHtml = '';
    try {
      const url = new URL(
        `assets/terms/professionals-${lang}.html`,
        document.baseURI,
      ).toString();
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      this.termsCache[lang] = html;
      this.termsHtml = html;
    } catch {
      this.termsError = this.translationService.translate(
        'maccabi.register.termsLoadError',
      );
    } finally {
      this.termsLoading = false;
    }
  }

  /** Back from the terms viewer to the form it was opened from (state preserved).
   *  The setup wizard is a full view, not an overlay, so returning to it just
   *  closes the terms overlay. */
  closeTerms(): void {
    this.overlay = this.termsReturnOverlay === 'setup' ? null : this.termsReturnOverlay;
  }

  closeOverlay(): void {
    this.overlay = null;
    this.loginPassword = '';
    this.twoFaCode = '';
    this.registerPassword = '';
    this.registerPasswordConfirm = '';
    this.registerCode = '';
  }

  showTwoFactor(): void {
    this.startLogin();
  }

  private syncLoginFieldsToRegister(): void {
    if (!this.registerEmail.trim() && this.loginEmail.trim()) {
      this.registerEmail = this.loginEmail.trim();
    }
    if (!this.registerPhone.trim() && this.doctorPhone.trim()) {
      this.registerPhone = this.doctorPhone.trim();
    }
  }

  private syncRegisterFieldsToLogin(): void {
    if (!this.loginEmail.trim() && this.registerEmail.trim()) {
      this.loginEmail = this.registerEmail.trim();
    }
    if (!this.doctorPhone.trim() && this.registerPhone.trim()) {
      this.doctorPhone = this.registerPhone.trim();
    }
  }

  async startLogin(): Promise<void> {
    if (this.loginBusy) return;
    this.loginError = '';
    this.loginInfo = '';
    const email = this.loginEmail.trim().toLowerCase();
    // Validate the email FORMAT before attempting sign-in. Browser autofill can
    // drop a non-email (e.g. an org name saved for the email field) into this
    // field; without this the doctor got a confusing "login failed"/credentials
    // error instead of a clear "enter a valid email".
    if (!email || !this.loginPassword) {
      this.loginError = this.translationService.translate('maccabi.errors.fillAllRegisterFields');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      this.loginError = this.translationService.translate('maccabi.errors.invalidEmail');
      return;
    }
    // A login never opens the setup wizard (that runs once, at registration).
    this.pendingSetupWizard = false;
    this.loginBusy = true;
    try {
      const { data, error } = await this.authService.signInWithPassword(email, this.loginPassword);
      if (error || !data.session?.access_token) throw error || new Error('No access token');
      await this.loginService.handleLogin(data.session.access_token, this.brandService.config.brandName, 'doctor');
      await this.joinClinicIfPending();
      if (this.isKnownDevice(email)) {
        // Recognised device: the password is enough, no second factor.
        this.goToPatients();
        return;
      }
      // New device: email a one-time code (Supabase, no SMS) and gate entry on it.
      const otp = await this.authService.sendEmailOtp(email);
      if (otp.error) {
        this.loginError = this.friendlyError(otp.error, this.translationService.translate('maccabi.errors.emailCodeSendFailed'));
        return;
      }
      this.twoFaCode = '';
      this.overlay = 'twofa';
    } catch (error: any) {
      this.loginError = this.friendlyError(error, this.translationService.translate('maccabi.errors.loginFailed'));
    } finally {
      this.loginBusy = false;
    }
  }

  async resendEmailCode(): Promise<void> {
    if (this.twoFaSmsBusy) return;
    this.loginError = '';
    this.loginInfo = '';
    this.twoFaSmsBusy = true;
    try {
      const email = this.loginEmail.trim().toLowerCase();
      const otp = await this.authService.sendEmailOtp(email);
      if (otp.error) {
        this.loginError = this.friendlyError(otp.error, this.translationService.translate('maccabi.errors.emailCodeSendFailed'));
        return;
      }
      this.loginInfo = this.translationService.translate('maccabi.twofa.codeResent');
    } catch (error: any) {
      this.loginError = this.friendlyError(error, this.translationService.translate('maccabi.errors.emailCodeSendFailed'));
    } finally {
      this.twoFaSmsBusy = false;
    }
  }

  async verifyTwoFactor(): Promise<void> {
    if (this.verifyBusy) return;
    this.loginError = '';
    if (!this.twoFaCode.trim()) {
      this.loginError = this.translationService.translate('maccabi.errors.codeRequired');
      return;
    }
    this.verifyBusy = true;
    try {
      const email = this.loginEmail.trim().toLowerCase();
      const { error } = await this.authService.verifyEmailCode(email, this.twoFaCode.trim());
      if (error) {
        this.loginError = this.friendlyError(error, this.translationService.translate('maccabi.errors.codeInvalidOrExpired'));
        return;
      }
      // Remember this browser so the next login here skips the email code.
      this.markDeviceKnown(email);
      // Fresh registration lands in the one-time setup wizard; a login goes
      // straight to her patient list.
      if (this.pendingSetupWizard) {
        this.pendingSetupWizard = false;
        this.enterSetup();
      } else {
        this.goToPatients();
      }
    } catch (error: any) {
      this.loginError = this.friendlyError(error, this.translationService.translate('maccabi.errors.codeInvalidOrExpired'));
    } finally {
      this.verifyBusy = false;
    }
  }

  async resetDoctorPassword(): Promise<void> {
    if (this.loginBusy) return;
    this.loginError = '';
    this.loginInfo = '';
    if (!this.loginEmail.trim()) {
      this.loginError = this.translationService.translate('maccabi.errors.emailRequiredForReset');
      return;
    }
    this.loginBusy = true;
    try {
      const { error } = await this.authService.resetPassword(this.loginEmail.trim());
      if (error) throw error;
      this.loginInfo = this.translationService.translate('auth.errors.resetSent');
    } catch (error: any) {
      this.loginError = this.friendlyError(error, this.translationService.translate('maccabi.errors.passwordResetFailed'));
    } finally {
      this.loginBusy = false;
    }
  }

  // Generic solo-doctor sign-up: name + email + password, no SMS, no phone, no
  // license. The account is created against the email (Supabase) and we sign in
  // right away, so registration RETURNS A SESSION and lands her in her (empty)
  // patient list, never bouncing back to the login screen.
  async submitRegistration(): Promise<void> {
    if (this.registerBusy) return;
    this.registerError = '';
    const fullName = this.registerFullName.trim();
    const email = this.registerEmail.trim().toLowerCase();
    if (!fullName || !email || !this.registerPassword) {
      this.registerError = this.translationService.translate('maccabi.errors.fillAllRegisterFields');
      return;
    }
    // Validate the email FORMAT up front: without this an org name typed into the
    // email field (e.g. "Dana's clinic network") reaches Supabase, which rejects
    // it, and the doctor only saw the generic "unable to create account" error.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      this.registerError = this.translationService.translate('maccabi.errors.invalidEmail');
      return;
    }
    if (this.registerPassword.length < 8) {
      this.registerError = this.translationService.translate('maccabi.errors.passwordTooShort');
      return;
    }
    if (this.registerPassword !== this.registerPasswordConfirm) {
      this.registerError = this.translationService.translate('maccabi.errors.passwordMismatch');
      return;
    }
    if (!this.registerTermsAccepted) {
      this.registerError = this.translationService.translate('maccabi.errors.mustAcceptTerms');
      return;
    }
    if (this.inviteOnly) {
      this.registerError = this.translationService.translate('maccabi.errors.inviteOnly');
      return;
    }

    this.registerBusy = true;
    // After the email-code step, open the one-time setup wizard (not the list).
    this.pendingSetupWizard = true;
    try {
      await this.maccabiService.createDoctorAccount({
        fullName,
        email,
        password: this.registerPassword,
        brand: this.brandService.config.isMarkable ? 'markable' : 'peritale',
        lang: this.translationService.getCurrentLanguage() === 'he' ? 'he' : 'en',
        // The confirm link usually opens in another tab (no sessionStorage):
        // the server keeps the pending clinic join and applies it on her first
        // confirmed login, then the link lands her in the setup wizard.
        clinicSlug: this.clinicCfg?.slug || this.clinicSlug || null,
        memberInviteToken: this.memberInviteToken || this.clinics.getPendingMemberInvite() || null,
      });
      // The account is created UNCONFIRMED: she proves she owns the address by
      // clicking the confirmation email, which signs her in and lands her in the
      // doctor setup wizard (signup_redirect). Until then the server refuses the login.
      const sent = await this.authService.resendSignupConfirmation(email);
      if (sent.error) {
        this.registerError = this.friendlyError(sent.error, this.translationService.translate('maccabi.errors.emailCodeSendFailed'));
        return;
      }
      this.pendingSetupWizard = false;
      this.loginEmail = email;
      this.loginInfo = this.translationService.translate('maccabi.register.checkEmail');
      this.overlay = 'login';
      return;
    } catch (error: any) {
      this.registerError = this.friendlyError(error, this.translationService.translate('maccabi.errors.accountCreateFailed'));
    } finally {
      this.registerBusy = false;
    }
  }

  onLoginPasswordKey(e: KeyboardEvent): void {
    try {
      this.loginCapsOn = e.getModifierState('CapsLock');
    } catch {
      this.loginCapsOn = false;
    }
  }

  goToPatients(): void {
    this.overlay = null;
    this.router.navigate(this.doctorPortalRoute('patients'));
    this.loadPatients();
    this.loadPersonalLink();
    this.syncPatientListLiveUpdates();
  }

  /** True while a magic-link / OAuth sign-in is (or is about to be) bridged into a
   *  server session: a token still sits in the URL, or the Supabase session is
   *  already parsed but not yet exchanged. In that window we must NOT flash the
   *  login card or navigate to /login (navigating strips the #access_token before
   *  Supabase reads it) — bridgePortalSession signs her in and lands her instead. */
  private authBridgePending(): boolean {
    if (this.portalBridgeBusy) return true;
    if (this.hasIncomingAuthInUrl()) return true;
    return (
      !!this.authService.session$.value?.access_token &&
      !this.sessionService.session
    );
  }

  private hasIncomingAuthInUrl(): boolean {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    return /access_token=|refresh_token=/.test(hash) || /[?&]code=/.test(search);
  }

  /** Consume an implicit-flow magic-link fragment (#access_token&refresh_token)
   *  that supabase-js won't auto-process because the client runs flowType:'pkce'
   *  (it rejects implicit callbacks in detectSessionInUrl). Exchanges the token
   *  for an app server session, lands on the patient list, and strips the tokens
   *  from the URL. No-op when there is no such fragment. Returns true if a
   *  fragment was found and handled. */
  private async consumeMagicLinkFragment(): Promise<boolean> {
    const hash = (window.location.hash || '').replace(/^#/, '');
    if (!/(?:^|&)access_token=/.test(hash)) return false;
    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    if (!accessToken || !refreshToken) return false;
    if (this.portalBridgeBusy || this.sessionService.session) return true;
    this.portalBridgeBusy = true;
    // Strip the tokens from the URL early to minimise how long the JWT is visible
    // in the address bar (the authoritative strip runs after navigation below).
    this.stripUrlFragment();
    try {
      // Exchange the magic-link access token for an app SERVER session. That is
      // all the doctor portal needs — every portal API call and the route gate
      // use sessionService.session, and the doctor routes carry no auth/Supabase
      // guard (only languageGuard). We deliberately do NOT call
      // authService.setSession here: supabase-js (flowType 'pkce') has already
      // rejected this implicit fragment in its boot detectSessionInUrl, and
      // racing a second setSession against that boot under the passthrough auth
      // lock caused a nondeterministic full-page reload that dropped the session.
      await this.loginService.handleLogin(
        accessToken,
        this.brandService.config.brandName,
        'doctor',
      );
      await this.joinClinicIfPending();
      // Land on the patient list, then strip the tokens from the URL. The strip
      // must run AFTER the navigation settles: Angular restores the fragment it
      // captured at bootstrap during navigation, so an earlier strip alone is
      // undone. replaceUrl drops the token URL from history entirely.
      this.overlay = null;
      await this.router.navigate(this.doctorPortalRoute('patients'), {
        replaceUrl: true,
      });
      this.stripUrlFragment();
      this.loadPatients();
      this.loadPersonalLink();
      this.syncPatientListLiveUpdates();
    } catch {
      // Expired / invalid magic link, or the server rejected it: fall back to the
      // login card so she can request a fresh link or sign in.
      this.view = 'landing';
      this.openLogin();
      this.router.navigate(this.doctorPortalRoute('login'), { replaceUrl: true });
    } finally {
      this.portalBridgeBusy = false;
    }
    return true;
  }

  /** Remove any auth token fragment (#access_token=...) left in the address bar
   *  without a router navigation, so the JWT isn't kept in history / referrers. */
  private stripUrlFragment(): void {
    if (!window.location.hash) return;
    try {
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search,
      );
    } catch {}
  }

  /** Exchange an implicit-flow (magic-link / OAuth) Supabase session for an app
   *  server session on the doctor portal, then land on the patient list. No-op
   *  when there is nothing to bridge, when the component's own password / register
   *  / 2FA flow is already signing in, or when a server session already exists. */
  private async bridgePortalSession(session: Session | null): Promise<void> {
    const accessToken = session?.access_token;
    if (!accessToken) return;
    if (this.sessionService.session) return; // already signed in
    // The password login / registration / 2FA paths call handleLogin themselves.
    if (this.loginBusy || this.registerBusy || this.verifyBusy) return;
    if (this.portalBridgeBusy) return;
    // Don't pull a signed-out visitor off the marketing landing on a lingering
    // Supabase session — only bridge on an authed view or a fresh auth redirect.
    if (this.view === 'landing' && !this.hasIncomingAuthInUrl()) return;
    this.portalBridgeBusy = true;
    try {
      await this.loginService.handleLogin(
        accessToken,
        this.brandService.config.brandName,
        'doctor',
      );
      await this.joinClinicIfPending();
      this.goToPatients();
    } catch {
      // Expired / invalid magic link, or the server rejected it: fall back to the
      // login card so she can request a fresh link or sign in.
      this.openLogin();
    } finally {
      this.portalBridgeBusy = false;
    }
  }

  logoutBusy = false;

  // Sign the doctor out and return her to the doctor portal landing (NOT the
  // consumer /en/login, which is where the shared logout defaults).
  async doctorLogout(): Promise<void> {
    if (this.logoutBusy) return;
    this.logoutBusy = true;
    try {
      const lang = this.currentLang();
      await this.loginService.revokeServerSession();
      await this.sessionService.clearSessionAndLogout(
        this.clinicSlug ? `/${lang}/app/doctor/${this.clinicSlug}` : `/${lang}/app/doctor`,
      );
    } catch {
      // Sign-out failed (rare): re-enable so she can retry.
      this.logoutBusy = false;
    }
  }

  // ---- First-time setup wizard --------------------------------------------

  /** Greeting name for the first-day screen, from the session (never invented). */
  get doctorFirstName(): string {
    const user = this.sessionService.session?.user;
    return (user?.fname || '').trim() || this.doctorDisplayName;
  }

  /** Optional clinic name saved on the account, shown next to the doctor's name. */
  get doctorClinic(): string {
    return ((this.sessionService.session?.user as any)?.clinic || '').trim();
  }

  /** Optional professional credential saved on the account (free text). */
  get doctorCredential(): string {
    return ((this.sessionService.session?.user as any)?.credential || '').trim();
  }

  // The demo's US credential list (MD/DO/NP/PA-C/CNM/ND). Shown as a dropdown
  // ONLY on the American English version (en.json); the British (en-GB), India
  // (en-IN) and Hebrew (he) versions keep the free-text field, since these US
  // credentials have no local equivalent (spec: don't hardcode a non-US list).
  readonly credentialOptions = ['MD', 'DO', 'NP', 'PA-C', 'CNM', 'ND', 'Other'];

  get credentialIsDropdown(): boolean {
    return this.translationService.getResolvedLocale() === 'en';
  }

  private enterSetup(): void {
    this.overlay = null;
    this.setupStep = 1;
    this.setupClinic = this.doctorClinic;
    this.setupCredential = this.doctorCredential;
    this.setupFirstPatientEmail = '';
    this.setupTermsAccepted = false;
    this.setupMethod = 'email';
    this.view = 'setup';
    this.router.navigate(this.doctorPortalRoute('setup'));
    this.loadPersonalLink();
  }

  async setupContinue(): Promise<void> {
    if (this.setupBusy) return;
    this.setupBusy = true;
    try {
      // Persist the optional clinic name + credential (empty allowed) before step 2.
      await this.saveDoctorProfile();
      this.setupStep = 2;
    } finally {
      this.setupBusy = false;
    }
  }

  setupBack(): void {
    this.setupStep = 1;
  }

  skipSetup(): void {
    this.markSetupSeen();
    this.goToPatients();
  }

  async finishSetup(): Promise<void> {
    if (this.setupBusy) return;
    this.setupBusy = true;
    try {
      await this.saveDoctorProfile();
      // If she filled the optional first-patient email (and the email channel is
      // selected), send that invite now.
      const email = this.setupFirstPatientEmail.trim();
      if (email && this.setupChannelEmail) {
        try {
          const result = await this.maccabiService.createInvitation(
            '',
            email,
            '',
            this.shouldAutoSendInviteSms(),
            this.activeClinicSlug,
          );
          this.smsSent = !!result?.email?.sent;
          this.inviteSmsSkipped = !!result?.email?.skipped;
          this.lastInviteUrl = result?.inviteUrl || '';
        } catch {
          // Non-fatal: she still lands in her portal and can invite from there.
        }
      }
    } finally {
      this.setupBusy = false;
      this.markSetupSeen();
      this.goToPatients();
    }
  }

  private async saveDoctorProfile(): Promise<void> {
    const clinic = this.setupClinic.trim();
    const credential = this.setupCredential.trim();
    if (clinic === this.doctorClinic && credential === this.doctorCredential) return;
    try {
      const result = await this.maccabiService.saveProfile(clinic, credential);
      const session = this.sessionService.session;
      if (session?.user && result?.user) {
        (session.user as any).clinic = result.user.clinic ?? clinic;
        (session.user as any).credential = result.user.credential ?? credential;
      }
    } catch {
      // Non-fatal: clinic/credential are optional; don't block the wizard on them.
    }
  }

  /** Load (create-or-get) the doctor's reusable personal invite link. */
  async loadPersonalLink(): Promise<void> {
    if (this.personalLink || this.personalLinkLoading) return;
    if (!this.sessionService.session) return;
    this.personalLinkLoading = true;
    try {
      const res = await this.maccabiService.getInviteLink(this.activeClinicSlug);
      this.personalLink = res?.inviteUrl || '';
    } catch {
      this.personalLink = '';
    } finally {
      this.personalLinkLoading = false;
    }
  }

  /**
   * Stamp the doctor's current portal language onto an invite link (?lang=he|en)
   * so the patient opens the approve flow in the language the doctor is viewing,
   * not the Markable Hebrew default. inviteRedirect (/i/:token) reads it. A
   * logged-in patient's own saved language still wins, so this only steers a
   * fresh (incognito / not-logged-in) visitor.
   */
  langLink(url: string): string {
    if (!url) return url;
    const lang = this.translationService.getCurrentLanguage();
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}lang=${lang}`;
  }

  async copyPersonalLink(): Promise<void> {
    if (!this.personalLink) return;
    const link = this.langLink(this.personalLink);
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      window.prompt(
        this.translationService.translate('maccabi.invite.copyPromptLabel'),
        link,
      );
    }
    this.personalLinkCopied = true;
    setTimeout(() => (this.personalLinkCopied = false), 2000);
  }

  private doctorSetupSeenKey(email: string): string {
    return `markable:doctorSetupSeen:${email.trim().toLowerCase()}`;
  }

  private isSetupSeen(): boolean {
    const email = this.sessionService.session?.user?.email;
    if (!email) return false;
    try {
      return localStorage.getItem(this.doctorSetupSeenKey(email)) === '1';
    } catch {
      return false;
    }
  }

  private markSetupSeen(): void {
    const email = this.sessionService.session?.user?.email;
    if (!email) return;
    try {
      localStorage.setItem(this.doctorSetupSeenKey(email), '1');
    } catch {
      /* noop */
    }
  }

  openPatient(patient: MaccabiPatient): void {
    if (!patient.guid) return;
    this.router.navigate(this.doctorPortalRoute('report', patient.guid), { state: { fromMaccabi: true } });
  }

  setFilter(filterValue: 'all' | MaccabiPatient['status']): void {
    this.statusFilter = filterValue;
  }

  async inviteAction(event: Event, patient?: MaccabiPatient): Promise<void> {
    event.stopPropagation();
    if (!patient?.inviteId || this.reminderBusy) return;
    this.reminderBusy = true;
    try {
      if (patient.inviteExpired) {
        await this.regenerateInvite(patient);
      } else {
        await this.sendReminder(patient);
      }
    } finally {
      this.reminderBusy = false;
    }
  }

  async sendReminder(patient: MaccabiPatient): Promise<void> {
    if (!patient.inviteId) return;
    // Explicit reminder tap = "actually send" button → force send even on dev/staging.
    const result = await this.maccabiService.remindInvitation(patient.inviteId, true);
    this.smsSent = !!result?.email?.sent;
    this.inviteSmsSkipped = !!result?.email?.skipped;
    this.lastInviteUrl = result?.inviteUrl || '';
  }

  async regenerateInvite(patient: MaccabiPatient): Promise<void> {
    if (!patient.inviteId) return;
    // Expired link → mint a fresh one-time token (1-month expiry) and send.
    const result = await this.maccabiService.regenerateInvitation(patient.inviteId, this.shouldAutoSendInviteSms());
    this.smsSent = !!result?.email?.sent;
    this.inviteSmsSkipped = !!result?.email?.skipped;
    this.lastInviteUrl = result?.inviteUrl || '';
    await this.loadPatients();
  }

  async copyInviteLink(event: Event, url?: string): Promise<void> {
    event.stopPropagation();
    if (!url) return;
    const link = this.langLink(url);
    try {
      await navigator.clipboard.writeText(link);
      this.copiedInviteUrl = url;
      setTimeout(() => {
        if (this.copiedInviteUrl === url) this.copiedInviteUrl = '';
      }, 2000);
    } catch {
      window.prompt(this.translationService.translate('maccabi.invite.copyPromptLabel'), link);
    }
  }

  async loadPatients(showLoading = true): Promise<void> {
    if (showLoading) {
      this.loadingPatients = true;
    }
    // The first-day screen shows the doctor's personal link; fetch it once.
    this.loadPersonalLink();
    try {
      const response = await this.maccabiService.getPatients(this.selectedClinic);
      this.memberships = (response.clinics || []) as ClinicMembership[];
      if (this.selectedClinic && !this.memberships.some((m) => m.clinic.slug === this.selectedClinic)) {
        this.selectedClinic = null;
      }
      // Apply the active clinic's branding (favicon + colour) on the portal even
      // when the URL has no slug: the clinic comes from the doctor's membership.
      // The co-brand logo already reads currentClinic, but the favicon/colour did
      // not, so an uploaded clinic favicon never showed on the post-login portal.
      const brandCfg = this.clinicCfg || this.currentClinic;
      if (brandCfg) this.clinics.applyBranding(brandCfg);
      this.pendingVerification = response.verified === false;
      this.pendingInvites = response.pendingInvites || [];
      this.patients = [
        ...(response.patients || []).map((patient, index) => this.toPatientCard(patient, index)),
        ...this.pendingInvites.map((invite) => this.toPendingInviteCard(invite)),
      ];
      // A held (unverified) account has no patients and no reports feed.
      if (!this.showPendingVerification) this.loadUpdates();
    } catch (error) {
      // Only bounce to the login overlay on a genuine auth failure (session
      // invalid / 2FA required). A transient network blip ("Failed to fetch")
      // or a 5xx must NOT throw the doctor out — keep her on the dashboard and
      // let the 30s background refresh + realtime recover. This is the core of
      // the "keeps kicking me to login" report.
      const status = (error as { status?: number })?.status;
      if (showLoading && (status === 401 || status === 403)) {
        this.patients = [];
        this.pendingInvites = [];
        this.openLogin();
      } else {
        console.warn('Maccabi patient refresh failed (kept on dashboard)', error);
      }
    } finally {
      if (showLoading) {
        this.loadingPatients = false;
      }
    }
  }

  async loadUpdates(): Promise<void> {
    if (!this.sessionService.session) return;
    try {
      const res = await this.maccabiService.getPatientUpdates();
      this.updates = res?.updates || [];
    } catch {
      // Non-fatal: the reports band just stays as-is on a transient failure.
      this.updates = this.updates || [];
    }
  }

  // Shown instead of the patient list while the account is held for verification.
  // QA/staging can preview the screen via ?pending=1 (display-only; the real gate
  // is the server verified flag, which also blocks the invite/remind endpoints).
  get showPendingVerification(): boolean {
    return this.pendingVerification || /[?&]pending=1(&|$)/.test(this.router.url);
  }

  updateWhen(u: MaccabiPatientUpdate): string {
    return u?.created ? new Date(u.created).toLocaleDateString('he-IL') : '';
  }

  updateMeta(u: MaccabiPatientUpdate): string {
    const sectionLabel = (s: string) =>
      this.translationService.translate('maccabi.updates.sections.' + s);
    return [(u.sections || []).map(sectionLabel).join(' · '), this.updateWhen(u)]
      .filter(Boolean)
      .join(' · ');
  }

  openReview(u: MaccabiPatientUpdate): void {
    this.reviewUpdate = u;
    this.reviewAgenda = false;
    this.reviewRecheck = false;
    this.reviewCopied = false;
  }

  closeReview(): void {
    this.reviewUpdate = null;
  }

  // The chart note carries HER words verbatim, never a conclusion of ours.
  chartNoteText(u: MaccabiPatientUpdate | null): string {
    if (!u) return '';
    const prefix = this.translationService.translate('maccabi.updates.selfReport');
    const sections = (u.sections || [])
      .map((s) => this.translationService.translate('maccabi.updates.sections.' + s))
      .join(', ');
    return `${prefix}${sections ? ' (' + sections + ')' : ''}: "${u.quote}"`;
  }

  async copyChartNote(): Promise<void> {
    const txt = this.chartNoteText(this.reviewUpdate);
    if (!txt) return;
    try {
      await navigator.clipboard.writeText(txt);
    } catch {
      window.prompt(this.translationService.translate('maccabi.updates.copy'), txt);
    }
    this.reviewCopied = true;
    setTimeout(() => (this.reviewCopied = false), 2000);
  }

  async createInvitation(): Promise<void> {
    if (this.inviteBusy) return;
    this.inviteError = '';
    // The invitation now travels by email, so the patient email is required
    // (the copy link is still shown as a fallback).
    if (!this.addEmail.trim()) {
      this.inviteError = this.translationService.translate('maccabi.addPatient.emailRequired');
      return;
    }
    this.inviteBusy = true;
    try {
      const result = await this.maccabiService.createInvitation(
        this.addPhone,
        this.addEmail,
        this.addDob,
        this.shouldAutoSendInviteSms(),
        this.activeClinicSlug,
      );
      this.smsSent = !!result?.email?.sent;
      this.inviteSmsSkipped = !!result?.email?.skipped;
      this.lastInviteUrl = result?.inviteUrl || '';
      this.showAddPatient = false;
      this.addPhone = '';
      this.addDob = '';
      this.addEmail = '';
      await this.loadPatients();
    } finally {
      this.inviteBusy = false;
    }
  }

  async loadInviteForApproval(): Promise<void> {
    this.approveToken = this.route.snapshot.params['token'] || '';
    if (!this.approveToken) return;
    try {
      this.approveInfo = await this.maccabiService.getInvitation(this.approveToken);
      this.approveStatus = '';
    } catch {
      this.approveStatus = this.translationService.translate('maccabi.approve.notFoundOrExpired');
    }
  }

  async approveDoctor(): Promise<void> {
    if (this.approveBusy) return;
    if (!this.sessionService.session) {
      this.sessionService.setRedirectUrl(this.router.url);
      // Preserve the visitor's locale rather than forcing /he, so an English
      // invite keeps English through login and back to approve.
      this.router.navigate(['/' + this.currentLang(), 'login']);
      return;
    }
    this.approveBusy = true;
    try {
      const result = await this.maccabiService.approveInvitation(this.approveToken);
      this.approveSucceeded = !!result?.approved;
      this.approveStatus = result?.approved
        ? this.translationService.translate('maccabi.approve.shareApproved', { name: result.doctorName })
        : this.translationService.translate('maccabi.approve.cannotApprove');
    } catch (error: any) {
      // The click must NEVER silently no-op. Surface a clear reason for every
      // failure (self-invite, expired, guard, network) instead of leaving the
      // woman on an unchanged screen thinking nothing happened.
      const status = error?.status;
      const code = String(error?.error?.message || error?.message || '');
      let key = 'maccabi.approve.cannotApprove';
      if (code.includes('cannot_link_self')) {
        key = 'maccabi.approve.selfInvite';
      } else if (status === 404 || /not available|not found|expired/i.test(code)) {
        key = 'maccabi.approve.notFoundOrExpired';
      }
      this.approveStatus = this.translationService.translate(key);
    } finally {
      this.approveBusy = false;
    }
  }

  /**
   * After the woman approves the share she was dead-ending on the confirmation
   * text with no way forward. Send her into the consumer test funnel so she can
   * actually take the Markable test the doctor invited her to do.
   */
  startMyTest(): void {
    this.router.navigate(['/' + this.currentLang(), 'app', 'new-test']);
  }

  private get filteredPatients(): MaccabiPatient[] {
    const q = this.query.trim().toLowerCase();
    return this.patients.filter((patient) => {
      const statusOk = this.statusFilter === 'all' || patient.status === this.statusFilter;
      const searchable = [
        patient.name,
        patient.email,
        patient.idNumber,
        patient.idLast4,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const queryOk = !q || searchable.includes(q);
      return statusOk && queryOk;
    });
  }

  private syncViewFromUrl(): void {
    // Match both the generic /app/doctor/* paths and the legacy /*/maccabi/*
    // paths (the latter now redirect to generic, but stay tolerant here).
    const url = this.router.url;
    // Clinic landing: /app/doctor/<slug> where <slug> is not a fixed segment.
    const slugMatch = url.match(/\/app\/doctor\/([a-z0-9-]+)(?:[/?#]|$)/i);
    const fixed = new Set(['login', 'setup', 'patients', 'approve', 'report']);
    const slug = slugMatch && !fixed.has(slugMatch[1].toLowerCase()) ? slugMatch[1].toLowerCase() : '';
    const memberInvite = this.route.snapshot.queryParamMap.get('memberInvite') || '';
    if (memberInvite && memberInvite !== this.memberInviteToken) {
      this.memberInviteToken = memberInvite;
      this.clinics.setPendingMemberInvite(memberInvite);
    }
    if (slug !== this.clinicSlug) {
      this.clinicSlug = slug;
      this.enterClinic(slug);
    }
    if (/\/(?:doctor|maccabi)\/approve\//.test(url)) {
      this.view = 'approve';
    } else if (/\/(?:doctor|maccabi)\/setup(?:$|[/?#])/.test(url)) {
      this.view = 'setup';
    } else {
      this.view = /\/(?:doctor|maccabi)\/patients/.test(url) ? 'patients' : 'landing';
    }
    if (this.view === 'setup') {
      // No session -> send her through login first. Already-completed setup ->
      // never re-open the wizard (spec: runs once); land on the patient list.
      if (!this.sessionService.session) {
        this.view = 'landing';
        this.openLogin();
        this.router.navigate(this.doctorPortalRoute('login'), { replaceUrl: true });
        return;
      }
      if (this.isSetupSeen()) {
        this.router.navigate(this.doctorPortalRoute('patients'), { replaceUrl: true });
        this.view = 'patients';
        return;
      }
      this.setupClinic = this.setupClinic || this.doctorClinic;
      this.loadPersonalLink();
      return;
    }
    if (
      this.view === 'patients' &&
      !this.sessionService.session &&
      !this.authBridgePending()
    ) {
      this.view = 'landing';
      this.openLogin();
      this.router.navigate(this.doctorPortalRoute('login'), { replaceUrl: true });
      return;
    }
    // Open the login card either on the real /app/doctor/login route or via the
    // legacy ?login=1 query param.
    if (
      this.view === 'landing' &&
      (/\/app\/doctor\/login(?:$|[/?#])/.test(url) ||
        this.route.snapshot.queryParamMap.get('login') === '1')
    ) {
      this.openLogin();
    }
  }

  private syncPatientListLiveUpdates(): void {
    if (this.view === 'patients' && this.sessionService.session) {
      this.startPatientListLiveUpdates();
    } else {
      this.stopPatientListLiveUpdates();
    }
  }

  private startPatientListLiveUpdates(): void {
    if (!this.patientRefreshTimer) {
      this.patientRefreshTimer = setInterval(() => {
        if (document.visibilityState === 'visible' && this.view === 'patients' && this.sessionService.session) {
          this.loadPatients(false);
        }
      }, this.backgroundRefreshMs);
    }

    if (!this.firebaseUnsubscribe && !this.firebaseConnectPromise) {
      this.firebaseConnectPromise = this.connectPatientListFirebaseUpdates()
        .catch((error) => console.warn('Maccabi realtime updates unavailable; using HTTP refresh fallback', error))
        .finally(() => {
          this.firebaseConnectPromise = null;
        });
    }
  }

  private stopPatientListLiveUpdates(): void {
    if (this.patientRefreshTimer) {
      clearInterval(this.patientRefreshTimer);
      this.patientRefreshTimer = null;
    }
    if (this.firebaseUnsubscribe) {
      this.firebaseUnsubscribe();
      this.firebaseUnsubscribe = null;
    }
  }

  private async connectPatientListFirebaseUpdates(): Promise<void> {
    const { token, docId } = await this.maccabiService.getRealtimeToken();
    await signInWithCustomToken(getAuth(), token);
    if (this.view !== 'patients' || !this.sessionService.session) {
      return;
    }
    const db = getFirestore(getApp());
    let firstSnapshot = true;
    this.firebaseUnsubscribe = onSnapshot(
      doc(db, 'maccabiDoctorPatientUpdates', docId),
      () => {
        if (firstSnapshot) {
          firstSnapshot = false;
          return;
        }
        this.zone.run(() => this.loadPatients(false));
      },
      (error) => {
        console.warn('Maccabi Firebase listener failed; using HTTP refresh fallback', error);
        this.firebaseUnsubscribe = null;
      },
    );
  }

  private toPatientCard(patient: any, index: number): MaccabiPatient {
    const date = patient.lastScanAt
      ? new Date(patient.lastScanAt).toLocaleDateString('he-IL')
      : this.translationService.translate('maccabi.patients.noScan');
    return {
      guid: patient.guid,
      linkId: patient.linkId,
      name: patient.name,
      idLast4: patient.idLast4 || (patient.idNumber ? String(patient.idNumber).slice(-4) : ''),
      idNumber: patient.idNumber || '',
      email: patient.email || '',
      status: patient.status,
      // Never render the patient's full email in the list (it opens in a room
      // with other people). Show a masked identifier instead, bidi-isolated so
      // the asterisks keep their side in RTL. Omit entirely when no id.
      meta:
        (patient.idLast4 || (patient.idNumber ? String(patient.idNumber).slice(-4) : '')
          ? this.translationService.translate('maccabi.patients.idLabel') +
            ' ⁦****' +
            (patient.idLast4 || String(patient.idNumber).slice(-4)) +
            '⁩ | '
          : '') +
        this.translationService.translate('maccabi.patients.cardMeta', {
          age: patient.age || '-',
          scanCount: patient.scanCount || 0,
          date,
        }),
      badge: patient.hasReport
        ? this.translationService.translate('maccabi.patients.badgeReportReady')
        : patient.guid
        ? this.translationService.translate('maccabi.patients.badgeDataExists')
        : this.translationService.translate('maccabi.patients.badgeWaitingData'),
      badgeBg: patient.hasReport ? '#e6f9f0' : '#fff4e0',
      badgeColor: patient.hasReport ? '#0a7c42' : '#8a5a00',
      dot: patient.hasReport ? '#0a7c42' : '#8a5a00',
      updated: patient.lastScanAt ? this.translationService.translate('maccabi.patients.lastScan', { date }) : undefined,
      group: index < 3 ? 'today' : 'more',
    };
  }

  private toPendingInviteCard(invite: MaccabiPendingInvite): MaccabiPatient {
    const expired = !!invite.expired;
    return {
      guid: null,
      name: invite.patientEmail || invite.phoneMasked,
      idLast4: invite.idLast4 || '',
      status: 'pending',
      meta: expired
        ? this.translationService.translate('maccabi.invite.metaExpired')
        : this.translationService.translate('maccabi.invite.metaPending'),
      badge: expired
        ? this.translationService.translate('maccabi.invite.badgeExpired')
        : this.translationService.translate('maccabi.invite.badgePending'),
      badgeBg: expired ? '#fdeaea' : '#eef0f2',
      badgeColor: expired ? '#b3261e' : '#555',
      dot: expired ? '#b3261e' : '#ddd',
      // SMS is not wired up yet, so the pending card exposes no "send SMS reminder"
      // action (Danna 2026-09-02). The manual invite link + Copy stays available below.
      // Expired invites keep the regenerate action (mints + emails a fresh link).
      updated: expired
        ? this.translationService.translate('maccabi.invite.actionRegenerate')
        : undefined,
      reminder: expired,
      group: 'more',
      inviteId: invite.inviteId,
      inviteUrl: invite.inviteUrl,
      inviteSent: (invite.smsStatus || '').startsWith('sent'),
      inviteExpired: expired,
    };
  }

  private hasUsableOtpChallenge(response: any): boolean {
    return !!response?.challengeId && (!!response?.sms?.sent || !!response?.debugCode || !!response?.sms?.skipped);
  }

  private shouldAutoSendOtpSms(): boolean {
    return environment.production || environment.env_name === 'Staging';
  }

  // Patient-join invites: only auto-send in prod. Dev + staging show the join link
  // and a "send SMS" button so test invites don't hit real patient phones.
  private shouldAutoSendInviteSms(): boolean {
    return environment.production;
  }

  private applyTwoFactorChallenge(challenge: any): void {
    this.twoFaChallengeId = challenge.challengeId;
    this.twoFaDebugCode = challenge.debugCode || '';
    this.twoFaSmsSkipped = !!challenge?.sms?.skipped;
  }

  private applyRegistrationChallenge(response: any): void {
    this.registerChallengeId = response.challengeId;
    this.registerPhoneMasked = response.phoneMasked || '';
    this.registerDebugCode = response.debugCode || '';
    this.registerSmsSkipped = !!response?.sms?.skipped;
  }

  /** A clinic landing opens in the CLINIC's default language, so an invite link
   *  that was minted with the wrong /en|/he segment still lands in the clinic's
   *  own language. Only for a visitor with no saved choice (same rule as the
   *  branded login page) - a woman who picked a language keeps it. */
  private applyClinicLanguage(cfg: ClinicPublicConfig): void {
    try {
      if (localStorage.getItem('app_language')) return;
      const lang = cfg.language === 'en' ? 'en' : 'he';
      if (lang === this.translationService.getCurrentLanguage()) return;
      const rest = this.router.url.replace(/^\/(?:en|he)(?=\/|$)/, '');
      this.router.navigateByUrl(`/${lang}${rest}`, { replaceUrl: true });
    } catch {
      /* noop */
    }
  }

  private doctorPortalRoute(...segments: string[]): string[] {
    // Generic doctor portal: no org segment in the path. The clinic (when any)
    // comes from the doctor's membership, never from an authed URL.
    return [`/${this.currentLang()}`, 'app', 'doctor', ...segments];
  }

  // ─── clinic helpers ──────────────────────────────────────────────────────

  private async enterClinic(slug: string): Promise<void> {
    if (!slug) {
      this.clinicCfg = null;
      this.clinics.leave();
      return;
    }
    const cfg = await this.clinics.enter(slug);
    if (cfg && this.clinics.ensureBrandHost(cfg)) return;
    this.clinicCfg = cfg && cfg.status === 'active' ? cfg : null;
    if (this.clinicCfg) this.applyClinicLanguage(this.clinicCfg);
    const token = this.memberInviteToken || this.clinics.getPendingMemberInvite() || '';
    if (token && this.clinicCfg) {
      try {
        this.memberInvitePreview = await this.clinics.getMemberInvite(token);
        this.memberInviteToken = token;
        const email = String(this.memberInvitePreview?.email || '');
        if (email) {
          if (!this.loginEmail) this.loginEmail = email;
          if (!this.registerEmail) this.registerEmail = email;
        }
        if (this.sessionService.session) {
          // Already signed in: accept the invite right away (the email must
          // match the invite); on mismatch show the login card with the reason.
          try {
            await this.clinics.join(this.clinicCfg.slug, token);
            this.clinics.setPendingMemberInvite(null);
            this.memberInviteToken = '';
            this.memberInvitePreview = null;
            this.goToPatients();
            return;
          } catch (e: any) {
            const code = String(e?.error?.message || '');
            this.loginError = this.translationService.translate(
              code === 'invite_email_mismatch'
                ? 'maccabi.clinic.inviteEmailMismatch'
                : 'maccabi.clinic.inviteJoinFailed',
              { email },
            );
            this.openLogin();
          }
        } else if (this.view === 'landing') {
          this.openLogin();
        }
      } catch {
        this.memberInvitePreview = null;
        this.memberInviteToken = '';
        this.clinics.setPendingMemberInvite(null);
      }
    }
  }

  /** After a doctor authenticates on a clinic landing: join by invite token, or
   *  self-join when the clinic allows it. Errors (already a member, invite for a
   *  different email, self-signup off) never block entering the portal. */
  private async joinClinicIfPending(): Promise<void> {
    const token = this.memberInviteToken || this.clinics.getPendingMemberInvite() || '';
    const slug = this.clinicCfg?.slug || this.clinicSlug;
    if (!slug) return;
    if (!token && !this.clinicCfg?.allowDoctorSelfSignup) return;
    try {
      await this.clinics.join(slug, token || null);
    } catch (e) {
      console.warn('clinic join skipped', e);
    } finally {
      this.clinics.setPendingMemberInvite(null);
      this.memberInviteToken = '';
      this.memberInvitePreview = null;
    }
  }

  /** The clinic the signed-in doctor is acting for (switcher selection, else first). */
  get currentClinic(): ClinicPublicConfig | null {
    if (this.selectedClinic) {
      return this.memberships.find((m) => m.clinic.slug === this.selectedClinic)?.clinic || null;
    }
    return this.memberships[0]?.clinic || null;
  }

  get activeClinicSlug(): string | null {
    return this.selectedClinic || this.currentClinic?.slug || null;
  }

  /** Co-brand: the landing clinic before login, the membership clinic after. */
  get showMaccabiCobrand(): boolean {
    return !!(this.clinicCfg || this.currentClinic);
  }

  get cobrandLogo(): string {
    return this.clinics.logoUrl(this.clinicCfg || this.currentClinic);
  }

  get orgLabel(): string {
    return this.clinics.displayName(this.clinicCfg || this.currentClinic);
  }

  /** Signup is closed on this clinic landing (admin invite only). */
  get inviteOnly(): boolean {
    return !!this.clinicCfg && !this.clinicCfg.allowDoctorSelfSignup && !this.memberInviteToken;
  }

  /**
   * i18n key for the primary CTA button on the doctor front.
   * Invite-only clinic → "Sign in"; a clinic landing with self-signup →
   * "Get my clinic link"; the generic (no-clinic) doctor front → "Get my link".
   */
  get ctaPrimaryKey(): string {
    if (this.inviteOnly) return 'maccabi.login.title';
    return this.clinicCfg ? 'maccabi.front.closerCtaPrimaryClinic' : 'maccabi.front.closerCtaPrimary';
  }

  get isClinicAdmin(): boolean {
    return this.memberships.some((m) => m.role === 'admin' && (m.status || 'active') === 'active');
  }

  get adminClinicSlug(): string {
    const m = this.memberships.find((x) => x.role === 'admin' && (x.status || 'active') === 'active');
    return m?.clinic.slug || '';
  }

  selectClinic(slug: string | null): void {
    this.selectedClinic = slug || null;
    this.personalLink = '';
    this.loadPatients();
  }

  openClinicAdmin(): void {
    const slug = this.selectedClinic && this.memberships.some((m) => m.clinic.slug === this.selectedClinic && m.role === 'admin')
      ? this.selectedClinic
      : this.adminClinicSlug;
    if (!slug) return;
    this.router.navigate([`/${this.currentLang()}`, 'clinic', slug, 'admin']);
  }

  async detachPatient(event: Event, patient: MaccabiPatient): Promise<void> {
    event.stopPropagation();
    if (!patient.linkId || this.detachBusyLinkId) return;
    if (!confirm(this.translationService.translate('maccabi.clinic.confirmDetach', { name: patient.name }))) return;
    this.detachBusyLinkId = patient.linkId;
    try {
      await this.maccabiService.detachPatient(patient.linkId);
      await this.loadPatients(false);
    } catch (e) {
      console.warn('detach failed', e);
    } finally {
      this.detachBusyLinkId = null;
    }
  }

  private currentLang(): string {
    const match = this.router.url.match(/^\/(en|he)(?:\/|$)/);
    return match?.[1] || 'he';
  }

  private rememberDoctorPortalSession(): void {
    try {
      sessionStorage.setItem(this.maccabiDoctorSessionKey, '1');
    } catch {
      /* noop */
    }
  }

  // "New device" = this browser has never completed a doctor login/registration
  // for this email. A recognised device skips the email second factor; a new one
  // requires the emailed code (spec: second factor only on a new device).
  private deviceKnownKey(email: string): string {
    return `markable:maccabiKnownDevice:${email.trim().toLowerCase()}`;
  }

  private isKnownDevice(email: string): boolean {
    try {
      return localStorage.getItem(this.deviceKnownKey(email)) === '1';
    } catch {
      return false;
    }
  }

  private markDeviceKnown(email: string): void {
    try {
      localStorage.setItem(this.deviceKnownKey(email), '1');
    } catch {
      /* noop */
    }
  }

  private friendlyError(error: any, fallback: string): string {
    const status = error?.status;
    const raw = String(error?.error?.message || error?.message || '');
    const rawLower = raw.toLowerCase();
    if (status === 0 || raw.includes('Http failure response')) {
      return this.translationService.translate('maccabi.errors.serverUnreachable');
    }
    if (rawLower.includes('invalid login credentials')) return this.translationService.translate('maccabi.errors.invalidCredentials');
    if (raw.includes('already exists')) return this.translationService.translate('maccabi.errors.accountAlreadyExists');
    if (raw.includes('rate limit') || raw.includes('Too many')) return this.translationService.translate('maccabi.errors.tooManyCodes');
    if (
      raw.includes('Invalid 2FA code') ||
      raw.includes('Invalid phone verification code') ||
      raw.includes('2FA challenge expired') ||
      raw.includes('Phone verification challenge expired')
    ) return this.translationService.translate('maccabi.errors.codeInvalidOrExpired');
    if (raw.includes('Valid Israeli mobile')) return this.translationService.translate('maccabi.errors.invalidIsraeliMobile');
    return fallback;
  }

  private smsFailureMessage(sms: any): string {
    const raw = String(sms?.error || '');
    if (
      raw.includes('Need to verify sender number') ||
      raw.includes('sender name or number is incorrect') ||
      raw.includes('Incorrect key') ||
      raw.includes('username or password') ||
      raw.includes('No SMS provider configured')
    ) {
      return this.translationService.translate('maccabi.errors.smsDeliveryProblem');
    }
    return this.translationService.translate('maccabi.errors.smsNotSentCheckProvider');
  }
}
